import { Clock } from 'lucide-react'
import * as React from 'react'
import { cn } from '../lib/cn'
import { Button } from './button'
import { Input } from './input'
import { Switch } from './switch'

/** Accepts `45`, `45m`, `1.5h`, `1h30m`, `1:30`. Returns minutes, or null when it can't tell. */
export function parseDuration(raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(/\s+/g, '')
  if (!s) return null
  const clock = s.match(/^(\d+):(\d{1,2})$/)
  if (clock) return Number(clock[1]) * 60 + Number(clock[2])
  const hours = s.match(/^(\d+(?:\.\d+)?)h(?:(\d+)m?)?$/)
  if (hours) return Math.round(Number(hours[1]) * 60 + Number(hours[2] ?? 0))
  const mins = s.match(/^(\d+)(?:m|min|mins)?$/)
  if (mins) return Number(mins[1])
  return null
}

export type TimeDomain = 'phone' | 'it' | 'macd'

/** One coverage domain's default (round 13): covered -> not billable, otherwise billable at the customer's rate. */
export interface TimeDomainOption {
  domain: TimeDomain
  billableDefault: boolean
  /** Why, verbatim ("Phone covered: Level 2 Easton"); an unknown says "coverage unknown", never "not covered". */
  basis?: string | null
  /** The customer's hourly rate; absent for roles that may not see money. */
  hourlyRate?: string | number | null
}

export interface TimeEntryDraft {
  minutes: number
  billable: boolean
  note: string
  /** Set when the prompt offered Phone / IT / MACD. */
  domain?: TimeDomain
}

const DOMAIN_LABEL: Record<TimeDomain, string> = { phone: 'Phone', it: 'IT', macd: 'MACD' }

function rateText(rate: string | number | null | undefined): string | null {
  if (rate === null || rate === undefined || rate === '') return null
  const n = Number(rate)
  if (!Number.isFinite(n)) return null
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}/h`
}

/**
 * The time prompt (scope §5.3): one inline bar, asked once per case (#186): on close, when the case's tracked time
 * passes the threshold, from the after-send "+ time" chip, or a manual entry. One field (defaulting to the tracked
 * time, with its source), billable defaulted from the coverage decision with its basis shown, a note, and (on close
 * and manual entry) "No time", which is a recorded choice with a reason.
 */
export function TimePrompt({
  moment = 'after send',
  defaultValue = '',
  defaultSource,
  billableDefault = false,
  billableBasis,
  domains,
  defaultDomain,
  onSave,
  onNoTime,
  onDismiss,
  allowNoTime = true,
  defaultNote = '',
  className,
}: {
  moment?: string
  defaultValue?: string
  defaultSource?: string
  billableDefault?: boolean
  billableBasis?: string
  /** Phone / IT / MACD with each one's billable default; the switch follows the chosen domain until flipped. */
  domains?: TimeDomainOption[]
  defaultDomain?: TimeDomain | null
  onSave: (entry: TimeEntryDraft) => void
  onNoTime: (reason: string) => void
  onDismiss?: () => void
  /** #186: the after-send and threshold moments ask nothing back; only close and a manual entry offer "No time". */
  allowNoTime?: boolean
  /** #509: a prefilled note (the call notes) the person can edit before saving. */
  defaultNote?: string
  className?: string
}) {
  const hasDomains = Boolean(domains?.length)
  const firstDomain = (defaultDomain && domains?.find((d) => d.domain === defaultDomain)) || domains?.[0]
  const [domain, setDomain] = React.useState<TimeDomain | undefined>(firstDomain?.domain)
  const option = domains?.find((d) => d.domain === domain)
  const [value, setValue] = React.useState(defaultValue)
  // A default that arrives or grows after mount (tracked time) follows until the person types their own.
  const valueTouched = React.useRef(false)
  React.useEffect(() => {
    if (!valueTouched.current) setValue(defaultValue)
  }, [defaultValue])
  const [billable, setBillable] = React.useState(option ? option.billableDefault : billableDefault)
  // Defaults that arrive after mount (the manual prompt fetches them) pre-pick once, unless the person already chose.
  const touched = React.useRef(false)
  const firstKey = firstDomain?.domain
  const firstBillable = firstDomain?.billableDefault
  React.useEffect(() => {
    if (touched.current || !firstKey || firstBillable === undefined) return
    setDomain(firstKey)
    setBillable(firstBillable)
  }, [firstKey, firstBillable])
  const basis = option ? (option.basis ?? undefined) : billableBasis
  const rate = billable ? rateText(option?.hourlyRate) : null
  const draft = (m: number): TimeEntryDraft =>
    hasDomains && domain ? { minutes: m, billable, note, domain } : { minutes: m, billable, note }
  const pickDomain = (d: TimeDomain) => {
    touched.current = true
    setDomain(d)
    const next = domains?.find((x) => x.domain === d)
    if (next) setBillable(next.billableDefault)
  }
  const [note, setNote] = React.useState(defaultNote)
  const [noTime, setNoTime] = React.useState(false)
  const [reason, setReason] = React.useState('')
  const minutes = parseDuration(value)
  const invalid = value.trim() !== '' && minutes === null

  return (
    <section
      aria-label="Log time"
      className={cn('flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary-soft px-3 py-2', className)}
    >
      <span className="flex items-center gap-1.5 text-sm font-medium text-primary">
        <Clock className="size-4" /> Time {moment}
      </span>
      {noTime ? (
        <>
          <Input
            autoFocus
            aria-label="Reason for no time"
            placeholder="Why no time? (e.g. courtesy reply, covered by packet)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="max-w-sm flex-1"
          />
          <Button variant="primary" size="sm" disabled={!reason.trim()} onClick={() => onNoTime(reason.trim())}>
            Record no time
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setNoTime(false)}>
            Back
          </Button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-1.5">
            <Input
              autoFocus
              aria-label="Minutes"
              aria-invalid={invalid || undefined}
              placeholder="45 or 1.5h"
              value={value}
              onChange={(e) => {
                valueTouched.current = true
                setValue(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && minutes) onSave(draft(minutes))
              }}
              className="w-24"
            />
            {minutes ? <span className="tabular text-xs text-muted-foreground">= {minutes} min</span> : null}
            {defaultSource ? <span className="text-xs text-subtle">({defaultSource})</span> : null}
          </div>
          {hasDomains ? (
            <div role="radiogroup" aria-label="Work type" className="inline-flex overflow-hidden rounded-md border border-border">
              {domains?.map((d) => (
                // biome-ignore lint/a11y/useSemanticElements: a segmented control; native radios cannot be styled as one
                <button
                  key={d.domain}
                  type="button"
                  role="radio"
                  aria-checked={d.domain === domain}
                  onClick={() => pickDomain(d.domain)}
                  className={cn(
                    'px-2.5 py-1 text-xs font-medium',
                    d.domain === domain ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-muted',
                  )}
                >
                  {DOMAIN_LABEL[d.domain]}
                </button>
              ))}
            </div>
          ) : null}
          <label className="flex items-center gap-1.5 text-sm" title={basis}>
            <Switch
              checked={billable}
              onCheckedChange={(v) => {
                touched.current = true
                setBillable(v)
              }}
              aria-label="Billable"
            />
            Billable
            {rate ? <span className="tabular text-xs text-muted-foreground">{rate}</span> : null}
            {basis ? <span className="text-xs text-subtle">· {basis}</span> : null}
          </label>
          <Input
            aria-label="Time note"
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="min-w-40 flex-1"
          />
          <Button variant="primary" size="sm" disabled={!minutes} onClick={() => minutes && onSave(draft(minutes))}>
            Log time
          </Button>
          {allowNoTime ? (
            <Button variant="outline" size="sm" onClick={() => setNoTime(true)}>
              No time
            </Button>
          ) : null}
          {onDismiss ? (
            <Button variant="ghost" size="sm" onClick={onDismiss} title="Nothing is recorded; the time stays tracked on this case">
              Not now
            </Button>
          ) : null}
        </>
      )}
    </section>
  )
}
